import { useRef, useState } from "react";
import { Heart, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { api } from "@/lib/api";
import { rp, thumb, stockLabel, stockColor } from "@/lib/format";
import { DiscountBadge, UrgencyChip, HematChip } from "@/components/PromoChips";
import T from "@/lib/testIds";

/**
 * Product card for grid/masonry view.
 * Fixed aspect-ratio image container — no layout collapse regardless of image dimensions.
 */
export default function ProductGridCard({ product, onOpenIntent }) {
  const [liked, setLiked] = useState(() => {
    try { return JSON.parse(localStorage.getItem("jt_likes") || "{}")[product.id] || false; } catch { return false; }
  });
  const [likeCount, setLikeCount] = useState(product.like_count || 0);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const lastLikeAt = useRef(0);

  const persistLike = (val) => {
    try {
      const store = JSON.parse(localStorage.getItem("jt_likes") || "{}");
      if (val) store[product.id] = true; else delete store[product.id];
      localStorage.setItem("jt_likes", JSON.stringify(store));
    } catch {}
  };

  const toggleLike = (e) => {
    e.stopPropagation();
    const now = Date.now();
    if (now - lastLikeAt.current < 500) return; // throttle 500ms
    lastLikeAt.current = now;
    if (liked) {
      setLiked(false); setLikeCount((c) => Math.max(0, c - 1)); persistLike(false);
      api.post("/events", { product_id: product.id, type: "unlike" }).catch(() => {});
    } else {
      setLiked(true); setLikeCount((c) => c + 1); persistLike(true);
      api.post("/events", { product_id: product.id, type: "like" }).catch(() => {});
    }
  };

  const doIntent = async (e) => {
    e.stopPropagation();
    if (product.stock_status === "sold_out") { toast.error("Barang sudah habis 😢"); return; }
    try { await api.post("/events", { product_id: product.id, type: "intent" }); } catch (err) {
      if (err?.response?.status === 409 || err?.response?.status === 410) toast.error("Barang sudah habis 😢");
      else toast.error("Koneksi bermasalah, coba lagi");
      return;
    }
    const prefersReduced = typeof window !== "undefined"
      && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (!prefersReduced) {
      confetti({ particleCount: 60, spread: 70, origin: { y: 0.7 }, colors: ["#F5A9C7", "#FFE8A6", "#BFD9F2"] });
    }
    onOpenIntent?.(product);
  };

  const photo = product.foto_urls?.[0] || "";
  const soldOut = product.stock_status === "sold_out";
  const diskon = product.diskon_persen || 0;
  const showCoret = product.harga_coret_idr && product.harga_coret_idr > product.harga_idr;

  return (
    <article
      data-testid={T.gridCard(product.id)}
      className="break-inside-avoid mb-3 rounded-3xl overflow-hidden relative bg-card border border-plum/10 shadow-dreamy hover:-translate-y-0.5 hover:shadow-[0_16px_36px_-12px_rgba(200,180,228,0.5)] transition-all duration-200 animate-card-in"
    >
      {/* Image — fixed aspect ratio, no collapse */}
      <div className="relative aspect-[4/5] overflow-hidden bg-plum/5 dark:bg-cream/5">
        {!imgLoaded && !imgError && <div className="absolute inset-0 shimmer-bg animate-shimmer" />}
        {photo && !imgError && (
          <img
            src={thumb(photo, 500)}
            alt={product.nama}
            loading="lazy"
            onLoad={() => setImgLoaded(true)}
            onError={() => { setImgError(true); setImgLoaded(true); }}
            className={`w-full h-full object-cover transition-opacity duration-300 ${imgLoaded ? "opacity-100 animate-img-zoom" : "opacity-0"}`}
          />
        )}

        {/* Top-left badges */}
        <div className="absolute top-2.5 left-2.5 flex flex-col gap-1 items-start z-10">
          {product.stock_status !== "preorder" && (
            <span className={`px-2.5 py-1 rounded-full text-[9px] font-semibold uppercase tracking-[0.18em] border border-white/60 backdrop-blur-sm ${stockColor(product.stock_status)}`}>
              {stockLabel(product.stock_status)}
            </span>
          )}
          <DiscountBadge persen={diskon} />
          <UrgencyChip soldOut={soldOut} diskon={diskon} stockStatus={product.stock_status} />
        </div>

        {/* Like button */}
        <button
          onClick={toggleLike}
          data-testid={T.gridCardLike(product.id)}
          aria-label="Like"
          className="absolute top-2.5 right-2.5 h-9 w-9 rounded-full grid place-items-center bg-white/85 backdrop-blur border border-plum/10 active:scale-90 transition-transform z-10"
        >
          <Heart className={`h-[15px] w-[15px] ${liked ? "fill-sakura-400 text-sakura-500 animate-heart-pop" : "text-plum/70"}`} strokeWidth={2} />
        </button>
      </div>

      {/* Info */}
      <div className="p-3.5">
        <p className="text-[9px] font-medium uppercase tracking-[0.2em] text-plum/50 dark:text-cream/50 mb-1">{product.kategori}</p>
        <h3 className="font-serif italic text-lg leading-tight line-clamp-2 mb-2 text-plum dark:text-cream">{product.nama}</h3>
        <div className="flex items-baseline gap-1.5 mb-1 flex-wrap">
          <span className="font-serif italic text-xl font-semibold text-sakura-600 dark:text-sakura-300">{rp(product.harga_idr)}</span>
          {showCoret && (
            <span className="text-[10px] text-sakura-400 dark:text-sakura-300/70 line-through">{rp(product.harga_coret_idr)}</span>
          )}
        </div>
        <div className="mb-2.5">
          <HematChip hargaCoret={product.harga_coret_idr} hargaJual={product.harga_idr} />
        </div>
        <button
          onClick={doIntent}
          disabled={soldOut}
          data-testid={T.gridCardIntent(product.id)}
          className={`w-full h-9 rounded-full font-semibold text-[11px] uppercase tracking-[0.2em] flex items-center justify-center gap-1.5 border border-plum/10 active:scale-95 transition-transform ${soldOut ? "bg-neutral-100 dark:bg-plum/20 text-plum/40 dark:text-cream/40 cursor-not-allowed" : "bg-sakura-400 hover:bg-sakura-500 text-plum shadow-[0_4px_16px_rgba(245,169,199,0.45)]"}`}
        >
          <ShoppingBag className="h-3.5 w-3.5" strokeWidth={2} />
          {soldOut ? "Habis" : "Beli"}
        </button>
        <div className="mt-2 flex items-center gap-1.5 text-[10px] text-plum/40 dark:text-cream/40">
          <Heart className="h-3 w-3" strokeWidth={1.8} /> {likeCount}
        </div>
      </div>
    </article>
  );
}
